/**
 * ApiError — a typed error wrapper for everything the axios api client
 * throws. Carries a SAFE-TO-DISPLAY `message` so callers can render it
 * directly without ever leaking raw `error.response.data` to the DOM.
 *
 * Why this exists:
 *   The booking-checkout confidentiality acknowledgement spec calls out
 *   a specific bug: an unauth submit returned 401 with body
 *   {"detail":"Not authenticated"} and the frontend rendered that JSON
 *   literally in the UI. Rule: no raw response body ever reaches the DOM.
 *
 * Pattern: every catch block does `const e = ApiError.from(err)` and
 * then reads `e.message` / `e.status` / `e.kind` — never `err.response.data`.
 */

const FRIENDLY_BY_STATUS = {
  400: "Something about that request looked off. Please try again.",
  401: "You need to be signed in to do that.",
  403: "You don't have permission to do that.",
  404: "We couldn't find that.",
  409: "Something changed since this page loaded. Please refresh and try again.",
  422: "Some of the information looked invalid. Please check and try again.",
  429: "Too many requests in a row. Please wait a moment and try again.",
  500: "Something went wrong on our side. Please try again.",
  502: "We can't reach our servers right now. Please try again in a moment.",
  503: "This service is temporarily unavailable. Please try again later.",
  504: "The request took too long. Please try again.",
};

const KIND_BY_STATUS = {
  401: "unauthenticated",
  403: "forbidden",
  404: "not_found",
  409: "conflict",
  422: "validation",
  429: "rate_limited",
};

export class ApiError extends Error {
  constructor({ status, kind, message, requestId, raw }) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.kind = kind;
    this.requestId = requestId;
    // `raw` is kept for logging only — never render this.
    this.raw = raw;
  }

  /**
   * Normalise any axios error (or fetch-style error) into an ApiError.
   * Always returns an ApiError instance. Never throws.
   */
  static from(err) {
    if (err instanceof ApiError) return err;

    // Network failure (no response) — axios sets `request` but not `response`.
    if (err && err.request && !err.response) {
      return new ApiError({
        status: 0,
        kind: "network",
        message: "Check your connection and try again.",
        raw: err.message,
      });
    }

    const status = err?.response?.status;
    const data = err?.response?.data;
    const requestId =
      err?.response?.headers?.["x-request-id"] ||
      err?.response?.headers?.["x-correlation-id"] ||
      null;

    // Special: backend uses a `code` discriminator for known error
    // categories. Two shapes are supported:
    //   • `data.code`        — used by BR-checkout-ack endpoints that
    //                          return a top-level JSON object
    //   • `data.detail.code` — used by FastAPI HTTPException(detail={...})
    //                          which wraps the dict under "detail" by
    //                          convention (e.g. POST /api/reviews returns
    //                          session_in_future / already_reviewed this way)
    // Both surface as `kind` so callers branch on one canonical field.
    let kind = KIND_BY_STATUS[status] || (status >= 500 ? "server" : "client");
    const candidate =
      (data && typeof data === "object" && typeof data.code === "string"
        ? data.code
        : null) ||
      (data && typeof data === "object" && data.detail
       && typeof data.detail === "object" && typeof data.detail.code === "string"
        ? data.detail.code
        : null);
    if (candidate && /^[a-z0-9_]{1,64}$/.test(candidate)) {
      // Trust the code only when it's a short ascii slug — never display raw.
      kind = candidate;
    }

    const friendly =
      FRIENDLY_BY_STATUS[status] || "Something went wrong. Please try again.";

    return new ApiError({
      status: status || 0,
      kind,
      message: friendly,
      requestId,
      raw: typeof data === "string" ? data.slice(0, 200) : safeStringify(data),
    });
  }

  /** True if this error means "your session is gone / never existed." */
  get isAuthError() {
    return this.status === 401;
  }
}

function safeStringify(v) {
  try {
    return JSON.stringify(v)?.slice(0, 200);
  } catch {
    return null;
  }
}

/**
 * BR-checkout-ack — classify an ApiError into the next state for the
 * confidentiality-acknowledgement state machine.
 *
 * Returning a string here instead of dispatching directly keeps the
 * function pure + testable: feed it an ApiError, get the next state.
 * The component owns the actual state transition.
 *
 * Returns one of:
 *   'session_expired'   — 401, needs login + auto-retry on this surface
 *   'wrong_account'     — 403, ownership mismatch
 *   'version_mismatch'  — 409 with code='content_version_mismatch'
 *   'error_other'       — everything else (network, 404, 500, etc.)
 */
export function classifyAckError(apiError) {
  if (!apiError) return "error_other";
  if (apiError.status === 401) return "session_expired";
  if (apiError.status === 403) return "wrong_account";
  if (apiError.kind === "content_version_mismatch") return "version_mismatch";
  return "error_other";
}
